import React, { useRef } from 'react';
import styled from 'styled-components';
import { Link, useNavigate } from 'react-router-dom';
import Features from './Landing/Features';
import SmartReminders from './Landing/SmartReminders';
import AdvancedFilters from './Landing/AdvancedFilters';
import DataExports from './Landing/DataExports';
import PaymentMethods from './Landing/PaymentMethods';
import TagsSection from './Landing/TagsSection';
import CTASection from './Landing/CTASection';

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
`;

const Header = styled.header`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 40px;
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  background: rgba(255, 255, 255, 0.8);
  backdrop-filter: blur(10px);
  z-index: 1000;
  box-shadow: 0 2px 20px rgba(0, 0, 0, 0.1);
  border-bottom: 1px solid rgba(255, 255, 255, 0.2);
`;

const Logo = styled(Link)`
  font-size: 32px;
  font-weight: 800;
  background: linear-gradient(135deg, var(--primary) 0%, var(--primary-dark) 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  letter-spacing: -1px;
`;

const Nav = styled.nav`
  display: flex;
  gap: 40px;
  align-items: center;

  a {
    text-decoration: none;
    color: var(--text-primary);
    font-weight: 500;
    font-size: 16px;
    transition: all 0.2s;
    position: relative;
    
    &:after {
      content: '';
      position: absolute;
      bottom: -4px;
      left: 0;
      width: 0;
      height: 2px;
      background: var(--primary);
      transition: width 0.2s;
    }
    
    &:hover {
      color: var(--primary);
      
      &:after {
        width: 100%;
      }
    }
  }
`;

const Button = styled.button`
  padding: 12px 24px;
  border-radius: 50px;
  border: none;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  
  ${props => props.primary && `
    background: linear-gradient(135deg, var(--primary) 0%, var(--primary-dark) 100%);
    color: white;
    box-shadow: 0 4px 15px rgba(99, 102, 241, 0.4);
    
    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 6px 25px rgba(99, 102, 241, 0.6);
    }
  `}
  
  ${props => !props.primary && `
    background: transparent;
    color: var(--text-primary);
    border: 2px solid var(--primary);
    
    &:hover {
      background: var(--primary);
      color: white;
      transform: translateY(-2px);
    }
  `}
`;

const HeroSection = styled.div`
  padding: 180px 0 100px;
  text-align: center;
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: radial-gradient(circle at 50% 50%, rgba(99, 102, 241, 0.1) 0%, transparent 50%);
    animation: pulse 8s ease-in-out infinite;
  }

  @keyframes pulse {
    0% { transform: scale(1); opacity: 0.5; }
    50% { transform: scale(1.5); opacity: 0.8; }
    100% { transform: scale(1); opacity: 0.5; }
  }
`;

const HeroTitle = styled.h1`
  font-size: 64px;
  font-weight: 800;
  margin-bottom: 24px;
  line-height: 1.1;
  background: linear-gradient(135deg, var(--primary) 0%, var(--primary-dark) 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  animation: fadeIn 1s ease-out;

  @keyframes fadeIn {
    from { opacity: 0; transform: translateY(20px); }
    to { opacity: 1; transform: translateY(0); }
  }
`;

const HeroSubtitle = styled.p`
  font-size: 20px;
  color: var(--text-secondary);
  margin-bottom: 40px;
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
  line-height: 1.6;
  animation: fadeIn 1s ease-out 0.2s backwards;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 20px;
  justify-content: center;
  margin-bottom: 80px;
  animation: fadeIn 1s ease-out 0.4s backwards;
`;

const StatsContainer = styled.div`
  display: flex;
  justify-content: center;
  gap: 80px;
  margin-top: 80px;
  position: relative;
  z-index: 1;
  animation: fadeIn 1s ease-out 0.6s backwards;
`;

const StatItem = styled.div`
  text-align: center;
  background: rgba(255, 255, 255, 0.8);
  padding: 24px 40px;
  border-radius: 16px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  backdrop-filter: blur(10px);
  transition: transform 0.3s ease;

  &:hover {
    transform: translateY(-5px);
  }
`;

const StatNumber = styled.div`
  font-size: 48px;
  font-weight: 800;
  background: linear-gradient(135deg, var(--primary) 0%, var(--primary-dark) 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  margin-bottom: 8px;
`;

const StatLabel = styled.div`
  font-size: 16px;
  color: var(--text-secondary);
  text-transform: uppercase;
  letter-spacing: 1px;
  font-weight: 500;
`;

const FeaturesSection = styled.div`
  padding: 100px 0;
  background: white;
  position: relative;
  z-index: 1;
`;

const LandingPage = () => {
  const featuresRef = useRef(null);
  const navigate = useNavigate();

  const scrollToFeatures = (e) => {
    e.preventDefault();
    featuresRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleGetStarted = () => {
    navigate('/login', { replace: true });
  };

  return (
    <>
      <Header>
        <Logo to="/">Prenewal.</Logo>
        <Nav>
          <Link to="/login">Login</Link>
          <Link to="/signup">
            <Button primary>Get Started Free</Button>
          </Link>
        </Nav>
      </Header>

      <HeroSection>
        <Container>
          <HeroTitle>Track Subscriptions.<br />Stop Wasting Money.</HeroTitle>
          <HeroSubtitle>
            Take control of your subscriptions with Prenewal. Track, manage, and optimize
            your recurring expenses in one beautiful dashboard.
          </HeroSubtitle>
         

          <StatsContainer>
            <StatItem>
              <StatNumber>10K+</StatNumber>
              <StatLabel>Active Users</StatLabel>
            </StatItem>
            <StatItem>
              <StatNumber>$2M+</StatNumber>
              <StatLabel>Saved Yearly</StatLabel>
            </StatItem>
            <StatItem>
              <StatNumber>50K+</StatNumber>
              <StatLabel>Subscriptions Tracked</StatLabel>
            </StatItem>
          </StatsContainer>
        </Container>
      </HeroSection>

      <FeaturesSection ref={featuresRef}>
        <Container>
          <Features />
          <SmartReminders />
          <AdvancedFilters />
          <DataExports />
          <PaymentMethods />
          <TagsSection />
        </Container>
      </FeaturesSection>
      <CTASection />
    </>
  );
};

export default LandingPage; 